import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import { French } from "flatpickr/dist/l10n/fr.js";
import "flatpickr/dist/flatpickr.css";
import { Editor } from "@tinymce/tinymce-react";
import Button from "../../components/ui/Button";
import { updateProcurement } from "../../services/api/procurementService";
import { buildScoresTemplate, cleanHtml, formatCurrencyEUR, formatEuroGroup, replaceSmartQuotes } from "../procurement/utils/procurementForm.utils";
import CriterionScoreEditor from "../procurement/forms/attributions/CriterionScoreEditor";
import { RejectCategory } from "../../helpers/bidderHelper";
import { getPositionByStep, Step } from "../../helpers/stepHelper";
import { convertAmountToPretty, currencyToFloat, moneyMask } from "../../helpers/formatHelper";

const REJECT_CATEGORIES = [
  { value: RejectCategory.EXCLUSION, label: "Motifs d'exclusion" },
  { value: RejectCategory.IRREGULARITY, label: "Irrégularité de l'offre" },
  { value: RejectCategory.ABNORMAL_PRICE, label: "Prix anormalement bas ou élevé" },
  { value: RejectCategory.OTHER, label: "Autre" },
];

const INITIAL_FORM = {
  status: "NO_ANSWER",
  amountTVAC: null,
  amountDisplay: "",
  receivedDate: null,
  rejectCategory: "",
  rejectComment: "",
  scores: {},
};

export default function BidderOffer() {
  const { bidder, procurement, handleUpdateProcurement } = useOutletContext();
  const { uuidProcurement } = useParams();
  const [form, setForm] = useState(INITIAL_FORM);
  const [baseline, setBaseline] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState({});

  let rejectCategories = REJECT_CATEGORIES;
  if (procurement.criteriaSelection && procurement.criteriaSelection.length > 0) {
    rejectCategories = [
      ...REJECT_CATEGORIES.slice(0, 1),
      { value: RejectCategory.CRITERIA_SELECTION, label: "Critères de sélection" },
      ...REJECT_CATEGORIES.slice(1),
    ];
  }

  const currentOffer = bidder.offers[0];

  const criteria = useMemo(() => {
    const arr = procurement?.awardCriteria || procurement?.criteria || [];
    return arr.map((c, idx) => ({
      id: c.id ?? String(idx),
      label: c.label ?? c.name ?? `Critère ${idx + 1}`,
      weightingRate: Number.isFinite(c.weightingRate) ? Number(c.weightingRate) : null,
    }));
  }, [procurement]);

  const totalScore = useMemo(() => {
    return criteria.reduce((acc, c) => {
      const v = Number(form.scores?.[c.id]?.score);
      return acc + (Number.isInteger(v) ? v : 0);
    }, 0);
  }, [criteria, form.scores]);

  const setField = useCallback((name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);
  const setTouchedField = useCallback((name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);
  const setScore = useCallback((criterionId, field, value) => {
    setForm(prev => ({
      ...prev,
      scores: {
        ...prev.scores,
        [criterionId]: {
          ...prev.scores[criterionId],
          [field]: value,
        },
      },
    }));
  }, []);

  /* A chaque fois qu'on change de bidder, on réinitialise tout */
  useEffect(() => {
    if (bidder?.uuid) setBaseline(null);
  }, [bidder?.uuid]);

  useEffect(() => {
    if (!criteria.length || !bidder) return;

    const offer = currentOffer || bidder?.offer || null;

    const status = offer?.status || "NO_ANSWER";
    const amountVAT = Number.isFinite(offer?.amountVAT) ? Number(offer.amountVAT) : null;

    const tpl = buildScoresTemplate(criteria);

    const scoresFromOffer = (offer?.criteria || []).reduce((acc, c, idx) => {
      acc[criteria[idx]?.id || String(idx)] = {
        score: c.score,
        detail: c.description || "",
      };
      return acc;
    }, {});

    const initialForm = {
      ...INITIAL_FORM,
      status,
      amountTVAC: amountVAT,
      amountDisplay: Number.isFinite(amountVAT) ? formatEuroGroup(amountVAT) : "",
      reference: offer?.reference,
      receivedDate: offer?.submittedAt,
      rejectCategory: offer?.rejectCategory,
      rejectComment: offer?.reasons,
      scores: {
        ...tpl,
        ...scoresFromOffer,
      },
    };

    setForm(initialForm);
    setBaseline(initialForm);
  }, [bidder, currentOffer, criteria]);

  useEffect(() => {
    if (form.status !== "REJECTED") {
      if (form.rejectCategory) setField("rejectCategory", "");
      if (form.rejectComment) setField("rejectComment", "");
    }
  }, [form.status, setField, form.rejectCategory, form.rejectComment]);

  const handleAmountInputChange = useCallback(
    (e) => {
      let currency = moneyMask(e.target.value);
      let number = currencyToFloat(currency);
      const maxTVAC = procurement?.type === "SMALL" ? 36300 : 173030; // TODO: nouveau montant au 1er janvier 2026 -> 169400
      number = Math.min(maxTVAC, Math.max(0, number));
      currency = moneyMask(convertAmountToPretty(number, { withCurrency: false }));

      setField("amountTVAC", number);
      setField("amountDisplay", currency);
      setTouchedField("amountTVAC");
    },
    [procurement?.type, setField, setTouchedField],
  );

  const handleAmountBlur = useCallback(() => {
    const amt = form.amountTVAC;
    if (!amt || amt === 0) {
      setField("amountTVAC", null);
      setField("amountDisplay", "");
    }
  }, [form.amountTVAC, setField]);

  const handleRejectEditorChange = useCallback(
    (newContent) => {
      setField("rejectComment", newContent);
      setTouchedField("rejectComment");
    },
    [setField, setTouchedField],
  );
  const handleRejectEditorKeyUp = useCallback((editor) => {
    const content = editor.getContent();
    const replaced = replaceSmartQuotes(content);
    if (content !== replaced) editor.setContent(replaced);
  }, []);

  const errors = useMemo(() => {
    const next = {};

    if (form.status !== "NO_ANSWER") {
      if (form.amountTVAC == null || !Number.isFinite(form.amountTVAC) || form.amountTVAC < 0) {
        next.amountTVAC = "Le montant TVAC est obligatoire.";
      } else if (procurement?.type === "SMALL" && form.amountTVAC > 36300) {
        next.amountTVAC = "Pour un marché de faible montant, le maximum TVAC est 36 300 €.";
      }

      if (!form.receivedDate) {
        next.receivedDate = "La date de réception est obligatoire.";
      } else if (form.receivedDate > new Date()) {
        next.receivedDate = "La date ne peut pas être dans le futur.";
      }
    }

    if (form.status === "REJECTED") {
      const rejectCategory = form.rejectCategory ?? "";
      const rejectComment = (form.rejectComment ?? "").trim();
      if (!rejectCategory) next.rejectCategory = "Sélectionnez une raison de rejet.";
      if (!rejectComment) next.rejectComment = "Expliquez la raison du rejet.";
    }

    if (form.status === "ANSWERED" || form.status === "SELECTED") {
      criteria.forEach((c) => {
        const entry = form.scores?.[c.id] || {};
        const s = entry.score;
        const d = (entry.detail ?? "").trim();
        const max = Number.isFinite(c.weightingRate) ? c.weightingRate : 100;

        const keyScore = `scores.${c.id}.score`;
        const keyDetail = `scores.${c.id}.detail`;

        if (s === "" || s === null || Number.isNaN(Number(s))) {
          next[keyScore] = "Score requis.";
        } else {
          const num = Number(s);
          if (!Number.isInteger(num)) {
            next[keyScore] = "Le score doit être un nombre entier.";
          } else if (num < 0 || num > max) {
            next[keyScore] = `Score entre 0 et ${max}.`;
          }
        }

        if (!d) {
          next[keyDetail] = "Détail requis.";
        }
      });

      if (totalScore < 0 || totalScore > 100) {
        next._scoresTotal = `Le total des scores doit être compris entre 0 et 100 (actuel : ${totalScore}).`;
      }
    }

    return next;
  }, [form, criteria, procurement?.type, totalScore]);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);
  const isDirty = useMemo(() => {
    if (!baseline) return true;
    return JSON.stringify(form) !== JSON.stringify(baseline);
  }, [form, baseline]);

  /* Quand on clique sur le bouton pour sauvegarder l'offre */
  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);

      const enterpriseNumber = bidder?.enterpriseNumber || "";

      const amountTVAC = Number(form.amountTVAC ?? 0);
      const amountExcludingVAT = Number(Number.isFinite(amountTVAC) ? (amountTVAC / 1.21).toFixed(2) : 0);

      const cleanedReject = cleanHtml(form.rejectComment || "");
      const reasons = cleanedReject.trim();

      let payload;
      if (form.status === "NO_ANSWER") {
        payload = {
          bidders: [
            {
              enterpriseNumber,
              offer: {
                status: "NO_ANSWER",
              },
            },
          ],
        };
      } else if (form.status === "REJECTED") {
        payload = {
          bidders: [
            {
              enterpriseNumber,
              offer: {
                status: "REJECTED",
                amountExcludingVAT: amountExcludingVAT,
                amountVAT: amountTVAC,
                rejectCategory: rejectCategories.find(r => r.value === form.rejectCategory)?.value,
                reasons: reasons,
                reference: form.reference,
                submittedAt: form.receivedDate,
              },
            },
          ],
        };
      } else {
        const criteriaArray = criteria.map(c => ({
          score: Number(form.scores?.[c.id]?.score ?? 0),
          description: cleanHtml((form.scores?.[c.id]?.detail ?? "").trim()),
        }));
        payload = {
          bidders: [
            {
              enterpriseNumber,
              offer: {
                status: form.status,
                amountExcludingVAT: amountExcludingVAT,
                amountVAT: amountTVAC,
                reference: form.reference,
                submittedAt: form.receivedDate,
                criteria: criteriaArray,
              },
            },
          ],
        };
      }

      await updateProcurement(uuidProcurement, payload);
      await handleUpdateProcurement();
      toast.success("Offre enregistrée");
      setBaseline(JSON.parse(JSON.stringify(form)));
    } catch (error) {
      toast.error(error?.message || "Erreur lors de l'enregistrement de l'offre");
    } finally {
      setIsSaving(false);
    }
  }, [bidder?.enterpriseNumber, form, uuidProcurement, handleUpdateProcurement, rejectCategories, criteria]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xl font-bold">{bidder?.name || "Soumissionnaire"}</h2>

        {form.status === "SELECTED" && (
          <span className="inline-flex items-center" title="Lauréat">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-7 h-7 text-yellow-500"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M4 19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7.382l-3.553 2.13a1 1 0 0 1-1.355-.33L12 6.618l-3.092 5.8a1 1 0 0 1-1.355.33L4 11.618V19zm-1 2a1 1 0 1 0 0 2h18a1 1 0 1 0 0-2H3z" />
            </svg>
          </span>
        )}
      </div>

      <div>
        <div className="block mb-2 text-sm font-medium">Statut</div>
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="status"
              value="SELECTED"
              checked={form.status === "SELECTED"}
              onChange={e => setField("status", e.target.value)}
            />
            <span>Lauréat</span>
          </label>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="status"
              value="ANSWERED"
              checked={form.status === "ANSWERED"}
              onChange={e => setField("status", e.target.value)}
            />
            <span>Recevable</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="status"
              value="REJECTED"
              checked={form.status === "REJECTED"}
              onChange={e => setField("status", e.target.value)}
            />
            <span>Rejetée</span>
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="radio"
              name="status"
              value="NO_ANSWER"
              checked={form.status === "NO_ANSWER"}
              onChange={e => setField("status", e.target.value)}
            />
            <span>N'a pas répondu</span>
          </label>
        </div>
      </div>

      {form.status !== "NO_ANSWER"
        ? (
          <div className="space-y-2 md:space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              <div>
                <label className="block mb-2 text-sm font-medium" htmlFor="amountTVAC">
                  Montant de l'offre <span className="text-red-500">*</span>
                  {procurement?.type === "SMALL" && (
                    <span className="text-xs text-gray-500 mt-1">
                      &nbsp; - Marché de faible montant : plafond de <strong>36 300 €&nbsp; TVAC</strong>
                    </span>
                  )}
                </label>

                <div className="relative">
                  <input
                    id="amountTVAC"
                    type="text"
                    inputMode="decimal"
                    value={form.amountDisplay}
                    onChange={handleAmountInputChange}
                    onBlur={handleAmountBlur}
                    className={`w-full border rounded px-3 py-2 pr-10 ${errors.amountTVAC && touched.amountTVAC ? "border-red-500" : "border-gray-300"}`}
                    placeholder="Ex: 125 000"
                  />
                  <span className="absolute right-3 top-5 -translate-y-1/2 text-gray-500 pointer-events-none">€</span>
                </div>

                {form.amountDisplay && (
                  <span className="text-xs text-gray-600 block pt-0">
                    Montant HTVA : <strong>{formatCurrencyEUR(Number.isFinite(form.amountTVAC) ? form.amountTVAC / 1.21 : 0)}</strong>
                  </span>
                )}

                <div className="min-h-[1.25rem] mt-0">
                  {errors.amountTVAC && touched.amountTVAC
                    ? (
                      <p className="text-red-600 text-sm">{errors.amountTVAC}</p>
                    )
                    : (
                      <span className="text-sm invisible">placeholder</span>
                    )}
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" htmlFor="receivedDate">
                  Date de réception <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <Flatpickr
                    value={form.receivedDate}
                    placeholder="jj-mm-aaaa"
                    options={{
                      enableTime: false,
                      dateFormat: "d-m-Y",
                      time_24hr: true,
                      locale: French,
                      maxDate: new Date(),
                    }}
                    onClose={([date]) => {
                      date.setHours(date.getHours() + 10); // fix timezone
                      setField("receivedDate", date);
                      setTouchedField("receivedDate");
                    }}
                    className={`w-full border rounded py-2 pl-10 pr-10 ${errors.receivedDate && touched.receivedDate ? "border-red-500" : "border-gray-300"}`}
                  />

                  <div className="absolute left-3 inset-y-0 flex items-center text-gray-400 pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>

                  {form.receivedDate && (
                    <button
                      type="button"
                      onClick={() => {
                        setField("receivedDate", "");
                        setTouchedField("receivedDate");
                      }}
                      className="absolute right-3 inset-y-0 flex items-center text-gray-400 hover:text-red-500"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="min-h-[1.25rem] mt-1">
                  {errors.receivedDate && touched.receivedDate
                    ? (
                      <p className="text-red-600 text-sm">{errors.receivedDate}</p>
                    )
                    : (
                      <span className="text-sm invisible">placeholder</span>
                    )}
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium" htmlFor="reference">
                  Références de l'offre
                </label>

                <div className="relative">
                  <input
                    id="reference"
                    type="text"
                    value={form.reference}
                    onChange={e => setField("reference", e.target.value)}
                    onBlur={() => setTouchedField("reference")}
                    className={`w-full border rounded px-3 py-2 pr-10 ${errors.reference && touched.reference ? "border-red-500" : "border-gray-300"}`}
                  />
                </div>

                <div className="min-h-[1.25rem] mt-0">
                  {errors.reference && touched.reference
                    ? (
                      <p className="text-red-600 text-sm">{errors.reference}</p>
                    )
                    : (
                      <span className="text-sm invisible">placeholder</span>
                    )}
                </div>
              </div>
            </div>

            {form.status === "REJECTED" && (
              <div className="-mt-8 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="block mb-2">
                  <label className="block mb-2 text-sm font-medium">Raison <span className="text-red-500">*</span></label>
                  <select
                    value={form.rejectCategory}
                    onChange={e => setField("rejectCategory", e.target.value)}
                    onBlur={() => setTouchedField("rejectCategory")}
                    className={`w-full border rounded p-2 ${errors.rejectCategory && touched.rejectCategory ? "border-red-500" : "border-gray-300"}`}
                  >
                    <option value="">— Sélectionner —</option>
                    {rejectCategories.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {errors.rejectCategory && touched.rejectCategory && (
                    <p className="text-red-600 text-sm mt-1">{errors.rejectCategory}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium">Explications <span className="text-red-500">*</span></label>
                  <div className={errors.rejectComment && touched.rejectComment ? "border border-red-500 rounded" : ""}>
                    <Editor
                      apiKey="tdmkfcmx36wjtguc5w8kcc91k37keduhkcroi36btoigb6wp"
                      value={typeof form.rejectComment === "string" ? form.rejectComment : ""}
                      onEditorChange={handleRejectEditorChange}
                      init={{
                        height: 360,
                        menubar: false,
                        plugins: "link visualblocks lists",
                        toolbar: "bold italic underline strikethrough | link | bullist numlist | removeformat",
                        toolbar_mode: "sliding",
                        paste_as_text: false,
                        paste_remove_styles: true,
                        paste_webkit_styles: "",
                        width: "100%",
                        language: "fr_FR",
                        statusbar: true,
                        branding: false,
                        elementpath: false,
                        resize: true,
                        placeholder: "Expliquez brièvement la raison du rejet.",
                        setup: (editor) => {
                          editor.on("PastePreProcess", (e) => {
                            let html = e.content;
                            html = replaceSmartQuotes(html);
                            e.content = html;
                          });
                          editor.on("keyup", () => handleRejectEditorKeyUp(editor));
                          editor.on("focusout", () => setTouchedField("rejectComment"));
                        },
                      }}
                    />
                  </div>
                  {errors.rejectComment && touched.rejectComment && (
                    <p className="text-red-600 text-sm mt-1">{errors.rejectComment}</p>
                  )}
                </div>
              </div>
            )}

            {(form.status === "ANSWERED" || form.status === "SELECTED") && (
              <fieldset className="border border-gray-300 p-4 rounded">
                <legend className="block text-base font-semibold bg-blue-100 px-4 py-1 leading-[1.6] rounded">
                  Notation par critère d'attribution
                </legend>

                {errors._scoresTotal && (
                  <div className="bg-red-100 text-red-700 px-3 py-2 rounded mb-2 font-medium">
                    ⚠️ {errors._scoresTotal}
                  </div>
                )}

                <div
                  className={`text-right font-semibold mb-2 ${errors._scoresTotal ? "text-red-600" : "text-green-600"}`}
                >
                  Total des scores : {totalScore.toFixed(0)}
                </div>

                {criteria.length === 0 && (
                  <p className="text-sm text-gray-600 mt-2">Aucun critère d'attribution défini pour ce marché.</p>
                )}

                <div className="mt-2 space-y-3">
                  {criteria.map(c => (
                    <CriterionScoreEditor
                      key={c.id}
                      criterion={c}
                      entry={form.scores?.[c.id] || { score: "", detail: "" }}
                      errorScore={errors[`scores.${c.id}.score`]}
                      errorDetail={errors[`scores.${c.id}.detail`]}
                      onChangeScore={setScore}
                      onBlurScore={setTouchedField}
                      onChangeDetail={setScore}
                      onBlurDetail={setTouchedField}
                    />
                  ))}
                </div>
              </fieldset>
            )}
          </div>
        )
        : (
          <div className="text-gray-700">
            Marqué comme <strong>n'a pas répondu</strong>.
          </div>
        )}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button
          onClick={handleSave}
          disabled={!isValid || isSaving || !isDirty || !procurement.canWrite || getPositionByStep[procurement.step.name] > getPositionByStep[Step.WRITING_ATTRIBUTION]}
          color="blue"
        >
          {isSaving ? "Enregistrement..." : "Enregistrer l'offre"}
        </Button>
      </div>
    </div>
  );
}
